import React, { useState, useEffect } from 'react';
import './PriceModal.css';

interface PriceModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceName: string;
  onSubmit: (email: string, serviceName: string) => Promise<void>;
}

/**
 * Modal component for requesting service price by email
 * @param isOpen - Controls visibility of the modal
 * @param onClose - Function to close the modal
 * @param serviceName - Name of the service user is requesting price for
 * @param onSubmit - Function to handle form submission
 */
const PriceModal: React.FC<PriceModalProps> = ({ 
  isOpen, 
  onClose, 
  serviceName, 
  onSubmit 
}) => {
  const [email, setEmail] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);

  // Сбрасываем состояние success при изменении isOpen или serviceName
  useEffect(() => {
    if (isOpen) {
      // Если модальное окно открывается, сбрасываем состояние ошибки
      setError('');
    } else {
      // Если модальное окно закрывается, сбрасываем все состояния
      setSuccess(false);
      setEmail('');
      setError('');
    }
  }, [isOpen, serviceName]);

  if (!isOpen) return null;

  /**
   * Handles form submission and email validation
   */
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Пожалуйста, введите корректный email');
      setIsSubmitting(false);
      return;
    }

    try {
      await onSubmit(email, serviceName);
      setSuccess(true);
    } catch (err) {
      setError('Произошла ошибка при отправке. Пожалуйста, попробуйте позже.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Функция для правильного закрытия модального окна и сброса состояний
  const handleClose = () => {
    onClose();
  };

  return (
    <div className="price-modal-overlay">
      <div className="price-modal-content">
        <button className="price-modal-close" onClick={handleClose}>×</button>
        <h3 className="price-modal-title">Узнать стоимость услуги</h3>
        <p className="price-modal-service">Услуга: <strong>{serviceName}</strong></p>
        
        {!success ? (
          <form onSubmit={handleSubmit} className="price-request-form">
            <div className="price-form-group">
              <label htmlFor="price-email">Ваш email:</label>
              <input
                type="email"
                id="price-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="example@mail.com"
                className="price-email-input"
              />
            </div>
            {error && <p className="price-error-message">{error}</p>}
            <button 
              type="submit" 
              className="price-submit-button" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Отправка...' : 'Получить стоимость'}
            </button>
          </form>
        ) : (
          <div className="price-success-message">
            <p>Спасибо!</p>
            <p>Информация о стоимости услуги отправлена на ваш email.</p>
            <p className="spam-notice">Пожалуйста, проверьте также папку "Спам" или "Нежелательная почта", если письмо не пришло в течение нескольких минут.</p>
            <button onClick={handleClose} className="price-close-success-button">Закрыть</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PriceModal; 